/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Badge, Button } from 'reactstrap';
import type { Recommendation } from '@g17eco/types/insights-synthesis';
import { TIME_HORIZON_LABELS } from '../utils/constants';
import { getSeverityInfo, getPriorityColor } from '../utils/helpers';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

interface RecommendationRowProps {
  action: Recommendation & {
    insightPriority?: 'low' | 'medium' | 'high' | 'critical';
    insightTitle?: string;
  };
  index: number;
  colors?: { number: string };
  /** When provided, shows a "View insight" link in the expanded section (for Recommendations dashboard) */
  onViewInsight?: () => void;
  selected?: boolean;
}

export const RecommendationRow: React.FC<RecommendationRowProps> = ({
  action,
  index,
  colors = { number: 'info' },
  onViewInsight,
  selected,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const impactScore = action.impactLevel || 0;
  const effortScore = action.effortRequired || 0;
  const controlScore = action.controlLevel || 0;

  return (
    <div className={`border-bottom py-3 px-2 ${selected ? 'background-ThemeSuccessExtralight' : ''}`}
      onClick={() => setIsExpanded(!isExpanded)}
      style={{
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-AccentExtralight)')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
    >
      <div className='d-flex align-items-start gap-2'>
        {/* Index Badge */}
        <div style={{ width: '2rem', flexShrink: 0 }}>
          <Badge
            color={selected ? 'success' : colors.number}
            className='text-lg rounded-circle d-flex align-items-center justify-content-center'
            style={{ width: '1.5rem', height: '1.5rem' }}
          >
            {index}
          </Badge>
        </div>

        {/* Expand indicator */}
        <div style={{ width: '1rem', flexShrink: 0 }} className='text-ThemeTextLight'>
          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
        </div>

        {/* Title and Description */}
        <div style={{ minWidth: '40%', flexShrink: 1 }}>
          {selected && <div className='text-sm text-ThemeSuccessMedium'><i className='fas fa-check-circle' /> Selected</div>}
          <div
            className='strong text-lg text-ThemeTextDark'
            style={isExpanded ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {action.title}
          </div>
          <div
            className='text-ThemeTextMedium'
            style={isExpanded ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {action.description}
          </div>
          {isExpanded && onViewInsight && (
            <div className='mt-2'>
              <Button
                color='link'
                onClick={(e) => {
                  e.stopPropagation();
                  onViewInsight();
                }}
              >
                <i className='fas fa-lightbulb me-1' />
                View full insight
              </Button>
            </div>
          )}
        </div>

        {/* Metadata Columns */}
        <div className='d-flex gap-2' style={{ flexShrink: 0 }}>
          {/* Impact Column */}
          <div className='text-center' style={{ minWidth: '4rem' }}>
            <SimpleTooltip text='Impact Level: The potential positive effect of implementing this recommendation'>
              <div className='text-sm text-ThemeTextLight mb-1'>Impact</div>
              <div className='text-ThemeTextDark strong'>
                <i className='fal fa-bullseye me-1 text-ThemeAccentMedium' />
                {getSeverityInfo(impactScore).level}
              </div>
            </SimpleTooltip>
          </div>

          {/* Effort Column */}
          <div className='text-center' style={{ minWidth: '4rem' }}>
            <SimpleTooltip text='Effort Required: The amount of resources, time, and work needed to implement this recommendation'>
              <div className='text-sm text-ThemeTextLight mb-1'>Effort</div>
              <div className='text-ThemeTextDark strong'>
                <i className='fal fa-hammer me-1 text-ThemeWarningMedium' />
                {getSeverityInfo(effortScore).level}
              </div>
            </SimpleTooltip>
          </div>

          {/* Control Column */}
          <div className='text-center' style={{ minWidth: '4rem' }}>
            <SimpleTooltip text='Control Level: How much control the organization has over implementing this recommendation'>
              <div className='text-sm text-ThemeTextLight mb-1'>Control</div>
              <div className='text-ThemeTextDark strong'>
                <i className='fal fa-hand-pointer me-1 text-ThemeSuccessMedium' />
                {getSeverityInfo(controlScore).level}
              </div>
            </SimpleTooltip>
          </div>

          {/* Owner Column */}
          <div className='text-center' style={{ minWidth: '5rem' }}>
            <SimpleTooltip text='Responsible Team: The team or department that should take ownership of this action'>
              <div className='text-sm text-ThemeTextLight mb-1'>Team</div>
              <div className='text-sm text-ThemeTextDark'>{action.responsibleTeam}</div>
            </SimpleTooltip>
          </div>

          {/* Timeline Column */}
          <div className='text-center' style={{ minWidth: '5rem' }}>
            <SimpleTooltip text='Time Horizon: The timeframe in which this recommendation should be implemented'>
              <div className='text-sm text-ThemeTextLight mb-1'>Timeline</div>
              <div className='text-sm text-ThemeTextDark'>
                <i className='fal fa-clock me-1 text-ThemeTextLight' />
                {TIME_HORIZON_LABELS[action.timeHorizon]}
              </div>
            </SimpleTooltip>
          </div>

          {/* Priority Column (optional) */}
          {action.insightPriority && (
            <div className='text-center' style={{ minWidth: '5rem' }}>
              <SimpleTooltip text='Priority: Overall importance of the parent insight based on impact, urgency, and strategic alignment'>
                <div className='text-sm text-ThemeTextLight mb-1'>Priority</div>
                <Badge color={getPriorityColor(action.insightPriority)}>{action.insightPriority}</Badge>
              </SimpleTooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
